import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Wrench } from 'lucide-react';
import { serviceColumns } from '../components/columns';
import { CarSelect } from '../components/forms/EntitySelects';
import { ServiceFormModal } from '../components/forms/ServiceFormModal';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Input, Select } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/Modal';
import { Tabs } from '../components/ui/Tabs';
import { useDeleteService, useServices, useUpcomingServices } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast } from '../store';
import type { Service, UpcomingService } from '../types';
import { formatCurrency, formatDate, formatNumber, SERVICE_TYPE_LABELS, toOptions } from '../utils/helpers';
import { t, tx } from '../i18n';

export function Services() {
  const [filters, setFilters] = useUrlFilters({
    tab: 'history',
    search: '',
    car_id: '',
    service_type: '',
    date_from: '',
    date_to: '',
    page: '1',
    page_size: '20',
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [deleting, setDeleting] = useState<Service | null>(null);
  const remove = useDeleteService();

  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 20)!;
  const { data, isLoading, isFetching } = useServices({
    search: filters.search,
    car_id: toInt(filters.car_id),
    service_type: filters.service_type,
    date_from: filters.date_from,
    date_to: filters.date_to,
    page,
    page_size: pageSize,
  });
  const { data: upcoming, isLoading: upcomingLoading } = useUpcomingServices(30);

  const upcomingColumns: Column<UpcomingService>[] = [
    {
      key: 'car',
      header: t('Vozidlo'),
      render: ({ service }) => (
        <div>
          <Link to={`/cars/${service.car_id}?tab=services`} className="font-mono font-semibold text-primary-700 hover:underline">
            {service.car?.license_plate}
          </Link>
          <p className="text-xs text-secondary-500">
            {service.car?.brand} {service.car?.model}
          </p>
        </div>
      ),
    },
    { key: 'last', header: t('Posledný servis'), render: ({ service }) => `${formatDate(service.service_date)} · ${SERVICE_TYPE_LABELS[service.service_type]}` },
    {
      key: 'due_date',
      header: t('Termín'),
      className: 'whitespace-nowrap',
      render: ({ service, days_left }) =>
        service.next_service_date ? (
          <>
            {formatDate(service.next_service_date)}
            <span className={days_left !== null && days_left < 0 ? 'ml-1 text-xs text-red-600' : 'ml-1 text-xs text-secondary-500'}>
              ({days_left !== null && days_left < 0 ? t('{daysLeft} dní po termíne', { daysLeft: -days_left }) : t('o {daysLeft} dní', { daysLeft: days_left })})
            </span>
          </>
        ) : (
          '–'
        ),
    },
    {
      key: 'due_km',
      header: t('Pri stave km'),
      className: 'whitespace-nowrap tabular-nums',
      render: ({ service, km_left }) =>
        service.next_service_km !== null ? (
          <>
            {formatNumber(service.next_service_km)}
            <span className={km_left !== null && km_left < 0 ? 'ml-1 text-xs text-red-600' : 'ml-1 text-xs text-secondary-500'}>
              ({km_left !== null && km_left < 0 ? t('prekročené o {kmLeft} km', { kmLeft: formatNumber(-km_left) }) : t('zostáva {kmLeft} km', { kmLeft: formatNumber(km_left) })})
            </span>
          </>
        ) : (
          '–'
        ),
    },
    { key: 'state', header: t('Stav'), render: ({ overdue }) => (overdue ? <Badge variant="danger">{t('Po termíne')}</Badge> : <Badge variant="warning">{t('Blíži sa')}</Badge>) },
  ];

  return (
    <>
      <PageHeader
        title={t('Servisy a údržba')}
        subtitle={t('Servisné záznamy, náklady a plánovaná údržba')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />  {t('Nový záznam')}
          </Button>
        }
      />
      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'history', label: t('Servisné záznamy') },
          { value: 'upcoming', label: t('Plánovaná údržba'), count: upcoming?.length },
        ]}
      />

      {filters.tab === 'upcoming' ? (
        <Card padding="none">
          <p className="border-b border-secondary-100 p-4 text-sm text-secondary-500">
            {t('Vozidlá, ktorých posledný servisný záznam plánuje ďalšiu údržbu do 30 dní alebo do 1 500 km (prípadne už po termíne).')}
          </p>
          <DataTable
            data={upcoming}
            columns={upcomingColumns}
            rowKey={(u) => u.service.id}
            isLoading={upcomingLoading}
            rowClassName={(u) => (u.overdue ? 'bg-red-50/50' : undefined)}
            emptyMessage={<EmptyState icon={<Wrench className="h-10 w-10" />} title={t('Žiadna blížiaca sa údržba')} />}
          />
        </Card>
      ) : (
        <Card padding="none">
          <FilterBar>
            <SearchInput value={filters.search} onChange={(search) => setFilters({ search })} placeholder={t('Popis, servis, faktúra…')} />
            <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-64" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
            <Select
              label={t('Typ')}
              wrapperClassName="sm:w-44"
              placeholder={t('Všetky typy')}
              options={toOptions(SERVICE_TYPE_LABELS)}
              value={filters.service_type}
              onChange={(e) => setFilters({ service_type: e.target.value })}
            />
            <Input label={t('Od')} type="date" wrapperClassName="sm:w-40" value={filters.date_from} onChange={(e) => setFilters({ date_from: e.target.value })} />
            <Input label={t('Do')} type="date" wrapperClassName="sm:w-40" value={filters.date_to} onChange={(e) => setFilters({ date_to: e.target.value })} />
          </FilterBar>
          {data && data.total > 0 && (
            <p className="border-b border-secondary-100 px-4 py-2 text-sm text-secondary-600">
              {tx('Náklady za vybrané záznamy: {sum}', { sum: <strong>{formatCurrency(data.total_cost)}</strong> })}
            </p>
          )}
          <DataTable
            data={data?.services}
            columns={serviceColumns({ onEdit: setEditing, onDelete: setDeleting, showCar: true })}
            rowKey={(s) => s.id}
            isLoading={isLoading}
            isFetching={isFetching}
            emptyMessage={<EmptyState icon={<Wrench className="h-10 w-10" />} title={t('Žiadne servisné záznamy')} />}
            pagination={
              data && {
                page,
                pageSize,
                total: data.total,
                onPageChange: (p) => setFilters({ page: String(p) }),
                onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
              }
            }
          />
        </Card>
      )}

      <ServiceFormModal isOpen={creating} onClose={() => setCreating(false)} carId={toInt(filters.car_id)} />
      <ServiceFormModal isOpen={!!editing} service={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title={t('Zmazať servisný záznam')}
        confirmText={t('Zmazať')}
        message={t('Naozaj zmazať servisný záznam {plate} zo dňa {serviceDate}?', { plate: deleting?.car?.license_plate ?? '', serviceDate: formatDate(deleting?.service_date) })}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(deleting!.id);
            toast.success(t('Záznam bol zmazaný'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setDeleting(null);
        }}
      />
    </>
  );
}
